export const UINT32_MAX = 0xffff_ffff;
export const MAX_PROTOCOL_STRING_LENGTH = 4096;
export const MAX_TOKEN_LENGTH = 8192;
export const MAX_CONTROL_MESSAGE_BYTES = 1024 * 1024;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;
const ISO_UTC_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;

export type ProtocolValidationCode =
  | "invalid_json"
  | "malformed"
  | "unknown_type"
  | "unsupported_version";

export class ProtocolValidationError extends Error {
  readonly code: ProtocolValidationCode;
  readonly path: string;

  constructor(
    code: ProtocolValidationCode,
    message: string,
    path = "$",
  ) {
    super(message);
    this.name = "ProtocolValidationError";
    this.code = code;
    this.path = path;
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function requireRecord(
  value: unknown,
  path = "$",
): Record<string, unknown> {
  if (!isRecord(value)) {
    throw malformed(path, "must be an object");
  }
  return value;
}

export function requireExactKeys(
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[] = [],
  path = "$",
): void {
  const allowed = new Set([...required, ...optional]);
  for (const key of required) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      throw malformed(`${path}.${key}`, "is required");
    }
  }
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) {
      throw malformed(`${path}.${key}`, "is not allowed");
    }
  }
}

export function requireLiteral<T extends string | number | boolean>(
  value: unknown,
  expected: T,
  path: string,
): T {
  if (value !== expected) {
    throw malformed(path, `must be ${JSON.stringify(expected)}`);
  }
  return expected;
}

export function requireEnum<T extends string>(
  value: unknown,
  values: readonly T[],
  path: string,
): T {
  if (typeof value !== "string" || !values.includes(value as T)) {
    throw malformed(path, `must be one of: ${values.join(", ")}`);
  }
  return value as T;
}

export function requireBoolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") {
    throw malformed(path, "must be a boolean");
  }
  return value;
}

export function requireString(
  value: unknown,
  path: string,
  options: {
    readonly allowEmpty?: boolean;
    readonly maxLength?: number;
  } = {},
): string {
  const maxLength = options.maxLength ?? MAX_PROTOCOL_STRING_LENGTH;
  if (
    typeof value !== "string"
    || (!options.allowEmpty && value.length === 0)
    || value.length > maxLength
  ) {
    throw malformed(
      path,
      `must be ${options.allowEmpty ? "a" : "a non-empty"} string of at most ${maxLength} characters`,
    );
  }
  return value;
}

export function requireNullableString(
  value: unknown,
  path: string,
  options: {
    readonly maxLength?: number;
  } = {},
): string | null {
  return value === null ? null : requireString(value, path, options);
}

export function requireInteger(
  value: unknown,
  path: string,
  options: {
    readonly min?: number;
    readonly max?: number;
  } = {},
): number {
  const min = options.min ?? Number.MIN_SAFE_INTEGER;
  const max = options.max ?? Number.MAX_SAFE_INTEGER;
  if (!Number.isSafeInteger(value) || Number(value) < min || Number(value) > max) {
    throw malformed(path, `must be an integer from ${min} to ${max}`);
  }
  return Number(value);
}

export function requireByteLength(value: unknown, path: string): number {
  return requireInteger(value, path, { min: 0, max: UINT32_MAX });
}

export function requireSequence(value: unknown, path: string): number {
  return requireInteger(value, path, { min: 0, max: UINT32_MAX });
}

export function requireUuid(value: unknown, path: string): string {
  const uuid = requireString(value, path, { maxLength: 36 });
  if (!UUID_PATTERN.test(uuid)) {
    throw malformed(path, "must be a lowercase RFC 4122 UUID");
  }
  return uuid;
}

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

export function requireSha256(value: unknown, path: string): string {
  const sha256 = requireString(value, path, { maxLength: 64 });
  if (!SHA256_PATTERN.test(sha256)) {
    throw malformed(path, "must be 64 lowercase hexadecimal characters");
  }
  return sha256;
}

export function isSha256(value: unknown): value is string {
  return typeof value === "string" && SHA256_PATTERN.test(value);
}

export function requireIsoTimestamp(value: unknown, path: string): string {
  const timestamp = requireString(value, path, { maxLength: 24 });
  if (!ISO_UTC_PATTERN.test(timestamp) || Number.isNaN(Date.parse(timestamp))) {
    throw malformed(path, "must be an ISO 8601 UTC timestamp");
  }
  return timestamp;
}

export function requireArray<T>(
  value: unknown,
  path: string,
  itemParser: (item: unknown, path: string) => T,
  options: {
    readonly minLength?: number;
    readonly maxLength?: number;
    readonly uniqueBy?: (item: T) => string;
  } = {},
): T[] {
  const minLength = options.minLength ?? 0;
  const maxLength = options.maxLength ?? 256;
  if (
    !Array.isArray(value)
    || value.length < minLength
    || value.length > maxLength
  ) {
    throw malformed(
      path,
      `must be an array with ${minLength} to ${maxLength} items`,
    );
  }
  const parsed = value.map((item, index) =>
    itemParser(item, `${path}[${index}]`)
  );
  if (options.uniqueBy) {
    const seen = new Set<string>();
    for (const item of parsed) {
      const key = options.uniqueBy(item);
      if (seen.has(key)) {
        throw malformed(path, `contains duplicate item ${JSON.stringify(key)}`);
      }
      seen.add(key);
    }
  }
  return parsed;
}

export function parseJsonObject(text: string): Record<string, unknown> {
  if (new TextEncoder().encode(text).byteLength > MAX_CONTROL_MESSAGE_BYTES) {
    throw malformed("$", "message exceeds the maximum encoded size");
  }
  try {
    return requireRecord(JSON.parse(text));
  } catch (error) {
    if (error instanceof ProtocolValidationError) {
      throw error;
    }
    throw new ProtocolValidationError(
      "invalid_json",
      "Message must be valid JSON",
    );
  }
}

export function assertNever(value: never, context: string): never {
  throw new ProtocolValidationError(
    "unknown_type",
    `Unknown ${context}: ${String(value)}`,
    "$.type",
  );
}

export function malformed(path: string, detail: string): ProtocolValidationError {
  return new ProtocolValidationError("malformed", `${path} ${detail}`, path);
}
